/**
 * "Enviar sugestão" (I4): texto livre + NPS opcional.
 *
 * O NPS só aparece quando o SERVIDOR diz que está na hora (`npsDue`, no máximo 1 vez a
 * cada 90 dias). Não responder é não responder: nenhuma nota é marcada por padrão, e o
 * servidor grava `null` — nunca um zero (invariante 12).
 */
import { useEffect, useId, useState } from 'react';
import { useLocation } from 'react-router';
import {
  Button,
  Dialog,
  Field,
  FormAlert,
  NativeSelect,
  Textarea,
  cn,
  useToast,
} from '@mgdlead/ui';

import { humanizeError } from '../../lib/errors';
import { useSendFeedback } from './experience-api';

export const FEEDBACK_MAX_LENGTH = 2000;

const CATEGORIES = [
  { value: 'SUGGESTION', label: 'Sugestão' },
  { value: 'PROBLEM', label: 'Algo não funcionou' },
  { value: 'PRAISE', label: 'Elogio' },
  { value: 'OTHER', label: 'Outro assunto' },
] as const;

type Category = (typeof CATEGORIES)[number]['value'];

/** Só o caminho do painel vai junto — nunca query, fragmento ou URL de fora. */
export function safePagePath(pathname: string): string | null {
  return /^\/[A-Za-z0-9/_-]{0,199}$/.test(pathname) ? pathname : null;
}

export interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  npsDue: boolean;
}

export function FeedbackDialog({ open, onOpenChange, workspaceId, npsDue }: FeedbackDialogProps) {
  const { toast } = useToast();
  const location = useLocation();
  const send = useSendFeedback();
  const npsLabelId = useId();
  const [category, setCategory] = useState<Category>('SUGGESTION');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Congelado na abertura: o NPS não some no meio do preenchimento. */
  const [askNps, setAskNps] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCategory('SUGGESTION');
    setMessage('');
    setScore(null);
    setError(null);
    setAskNps(npsDue);
    // só na abertura
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmed = message.trim();
  const canSend = trimmed.length > 0 || score !== null;

  const submit = () => {
    if (!canSend) {
      setError('Escreva a sugestão ou escolha uma nota.');
      return;
    }
    setError(null);
    send.mutate(
      {
        workspaceId,
        category,
        message: trimmed.length > 0 ? trimmed : null,
        npsScore: askNps ? score : null,
        npsAsked: askNps,
        pagePath: safePagePath(location.pathname),
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          toast({ title: 'Recebemos, obrigado.', tone: 'success' });
        },
        onError: (err) => setError(humanizeError(err)),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Enviar sugestão"
      description="O que melhoraria o seu dia no painel? Lemos tudo."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={submit} loading={send.isPending} disabled={!canSend}>
            Enviar
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {error ? (
          <FormAlert tone="danger" title="Não deu para enviar">
            {error}
          </FormAlert>
        ) : null}

        <Field label="Assunto">
          {(control) => (
            <NativeSelect
              {...control}
              value={category}
              onChange={(event) => setCategory(event.target.value as Category)}
              options={CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
            />
          )}
        </Field>

        <Field label="Sua mensagem">
          {(control) => (
            <Textarea
              {...control}
              value={message}
              maxLength={FEEDBACK_MAX_LENGTH}
              showCount
              rows={5}
              placeholder="Ex.: queria ver a evasão por anúncio na página da campanha."
              onChange={(event) => setMessage(event.target.value)}
            />
          )}
        </Field>

        {askNps ? (
          <fieldset className="flex flex-col gap-2">
            <legend id={npsLabelId} className="mb-2 text-sm text-foreground">
              De 0 a 10, quanto recomendaria o MGDLead?{' '}
              <span className="text-muted-foreground">(opcional)</span>
            </legend>
            <div
              role="radiogroup"
              aria-labelledby={npsLabelId}
              className="grid grid-cols-6 gap-1.5 sm:grid-cols-11"
            >
              {Array.from({ length: 11 }, (_, n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={score === n}
                  onClick={() => setScore(score === n ? null : n)}
                  className={cn(
                    'inline-flex min-h-11 items-center justify-center rounded-md text-sm tabular-nums ring-1 ring-inset md:min-h-9',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    score === n
                      ? 'bg-primary text-primary-foreground ring-primary'
                      : 'bg-surface text-foreground ring-border hover:ring-border-strong',
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
            <p className="flex justify-between text-2xs text-muted-foreground">
              <span>0 = nada provável</span>
              <span>10 = com certeza</span>
            </p>
          </fieldset>
        ) : null}
      </form>
    </Dialog>
  );
}
